import { TextInput, useRecordContext, InputHelperText, useTranslate } from 'react-admin';
import { TextField as MuiTextInput } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';

const AddressInput = ({ subform }) => {
    const { setValue, getFieldState } = useFormContext();
    const record = useRecordContext();
    const google_maps_api_key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const sourcePrefix = subform ? 'collectionPoint.' : '';

    const onPlaceSelect = useCallback(
        place => {
            const addressData = placeToAddress(place);

            setValue(`${sourcePrefix}country`, addressData.country);
            setValue(`${sourcePrefix}province`, addressData.province);
            setValue(`${sourcePrefix}city`, addressData.city);
            setValue(`${sourcePrefix}postalCode`, addressData.postalCode);
            setValue(`${sourcePrefix}address`, addressData.address);

            const isLatitudeDirty = getFieldState(`${sourcePrefix}latitude`).isDirty;
            const isLongitudeDirty = getFieldState(`${sourcePrefix}longitude`).isDirty;
            if (!isLatitudeDirty && !isLongitudeDirty && addressData.latitude != null && addressData.longitude != null) {
                setValue(`${sourcePrefix}latitude`, addressData.latitude, { shouldDirty: true });
                setValue(`${sourcePrefix}longitude`, addressData.longitude, { shouldDirty: true });
            }

            setValue(`${sourcePrefix}placeId`, addressData.placeId, { shouldDirty: true });
        },
        [getFieldState, sourcePrefix, setValue],
    );

    const defaultValue = record && !subform ? [record.address, record.city, record.country].join(', ') : '';

    return (
        <APIProvider apiKey={google_maps_api_key} libraries={['places']}>
            <PlaceSelect onPlaceSelect={onPlaceSelect} defaultValue={defaultValue} />
            <TextInput source={`${sourcePrefix}placeId`} readOnly sx={{ display: 'none' }} />
        </APIProvider>
    );
};

const PlaceSelect = ({ onPlaceSelect, defaultValue }) => {
    const [placeAutocomplete, setPlaceAutocomplete] = useState(null);
    const textRef = useRef(null);
    const places = useMapsLibrary('places');
    const translate = useTranslate();

    useEffect(() => {
        if (!places || !textRef.current) return;

        const options = {
            componentRestrictions: { country: 'pl' },
            types: ['address'],
            fields: ['place_id', 'address_components', 'geometry.location'],
        };

        setPlaceAutocomplete(new places.Autocomplete(textRef.current, options));
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            onPlaceSelect(placeAutocomplete.getPlace());
        });
    }, [placeAutocomplete, onPlaceSelect]);

    return (
        <MuiTextInput
            key={defaultValue}
            helperText={<InputHelperText />}
            inputRef={textRef}
            label={translate('common.address')}
            placeholder={translate('common.addressPlaceholder')}
            defaultValue={defaultValue}
            required
            sx={{ width: '100%' }}
        />
    );
};

AddressInput.propTypes = {
    subform: PropTypes.bool,
};

PlaceSelect.propTypes = {
    onPlaceSelect: PropTypes.func.isRequired,
    defaultValue: PropTypes.string,
};

const placeToAddress = place => {
    const componentsByType = {};
    for (const component of place.address_components) {
        for (const type of component.types) {
            componentsByType[type] = component.long_name;
        }
    }

    const streetNumber = componentsByType.street_number ?? componentsByType.premise;
    const streetAddress = [componentsByType.route, streetNumber].filter(Boolean).join(' ');

    return {
        country: componentsByType.country,
        province: (componentsByType.administrative_area_level_1 ?? '').replace('Województwo ', ''),
        city: componentsByType.locality,
        postalCode: componentsByType.postal_code,
        address: streetAddress ?? undefined,
        placeId: place.place_id,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
    };
};

export default AddressInput;
